// =====================================================
// Referral Tracking Utility
// =====================================================
// SHARED utility for all tools (GraveForge, Gatekeeper, DeathDraw)
// Copy this file to: src/utils/referralTracking.js in each tool
// =====================================================

export const REFERRAL_STORAGE_KEY = 'solanadeads_referral_code';

/**
 * Store referral code (first-touch only)
 * @param {string} code - Referral code from URL
 * @param {string} toolKey - Which tool (boneforge, gatekeeper, deathdraw)
 * @returns {boolean} - Whether code was stored
 */
export function storeReferralCode(code, toolKey) {
  try {
    const existing = getReferralCode();
    
    if (existing) {
      console.log(`[Referral] First code wins: ${existing.code} (ignoring ${code})`);
      return false; // Did not store (first-touch protection)
    }
    
    const referralData = {
      code: code.toLowerCase().trim(),
      timestamp: Date.now(),
      toolKey: toolKey,
      originalUrl: window.location.href
    };
    
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
    console.log(`[Referral] Stored first code: ${code}`);
    return true; // Stored successfully
    
  } catch (error) {
    console.error('[Referral] Error storing code:', error);
    return false;
  }
}

/**
 * Get stored referral code
 * @returns {object|null} - Referral data or null
 */
export function getReferralCode() {
  try {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('[Referral] Error reading code:', error);
    return null;
  }
}

/**
 * Clear referral code (after attribution)
 */
export function clearReferralCode() {
  localStorage.removeItem(REFERRAL_STORAGE_KEY);
  console.log('[Referral] Cleared stored code');
}

/**
 * Check if code is expired (optional - 30 days default)
 * @param {number} maxAgeMs - Max age in milliseconds
 * @returns {boolean}
 */
export function isReferralExpired(maxAgeMs = 30 * 24 * 60 * 60 * 1000) {
  const referral = getReferralCode();
  if (!referral) return true;
  
  return (Date.now() - referral.timestamp) > maxAgeMs;
}

/**
 * Validate referral code format
 * @param {string} code - Code to validate
 * @returns {boolean}
 */
export function isValidReferralCodeFormat(code) {
  if (!code || typeof code !== 'string') return false;
  
  const trimmed = code.trim().toLowerCase();
  
  // Length check
  if (trimmed.length < 4 || trimmed.length > 20) return false;
  
  // Format check (alphanumeric + hyphens/underscores only)
  return /^[a-z0-9_-]+$/.test(trimmed);
}

/**
 * Check attribution status for a wallet
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<object|null>} - Attribution data or null
 */
export async function checkAttribution(walletAddress) {
  try {
    const response = await fetch(`${getSupabaseUrl()}/functions/v1/referral-check-attribution`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getSupabaseAnonKey()}`
      },
      body: JSON.stringify({ walletAddress })
    });
    
    if (!response.ok) {
      // Edge function not deployed or error - fail silently
      console.log('[Referral] Attribution check unavailable (edge function not deployed)');
      return null;
    }
    
    const data = await response.json();
    return data.hasAttribution ? data : null;
  } catch (error) {
    // Network error or CORS - fail silently
    console.log('[Referral] Attribution check unavailable:', error.message);
    return null;
  }
}

/**
 * Create attribution for a wallet
 * @param {string} walletAddress - User's wallet address
 * @param {string} referralCode - Referral code from localStorage
 * @param {string} toolKey - Tool identifier
 * @returns {Promise<object>} - Attribution result
 */
export async function createAttribution(walletAddress, referralCode, toolKey) {
  try {
    const response = await fetch(`${getSupabaseUrl()}/functions/v1/referral-attribute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getSupabaseAnonKey()}`
      },
      body: JSON.stringify({ 
        walletAddress, 
        referralCode, 
        toolKey 
      })
    });
    
    return await response.json();
  } catch (error) {
    console.error('[Referral] Error creating attribution:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Track a transaction (purchase, subscription, etc.)
 * @param {object} params - Transaction parameters
 * @param {string} params.walletAddress - User's wallet address
 * @param {string} params.toolKey - Tool identifier
 * @param {string} params.transactionType - Type of transaction
 * @param {string} params.transactionId - Unique transaction ID
 * @param {number} params.amountUsd - Transaction amount in USD
 * @param {object} params.metadata - Additional metadata
 * @returns {Promise<object>} - Tracking result
 */
export async function trackTransaction({ 
  walletAddress, 
  toolKey, 
  transactionType, 
  transactionId, 
  amountUsd, 
  metadata = {} 
}) {
  try {
    const response = await fetch(`${getSupabaseUrl()}/functions/v1/referral-track-transaction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getSupabaseAnonKey()}`
      },
      body: JSON.stringify({ 
        walletAddress, 
        toolKey, 
        transactionType, 
        transactionId, 
        amountUsd, 
        metadata 
      })
    });
    
    return await response.json();
  } catch (error) {
    console.error('[Referral] Error tracking transaction:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Handle wallet connection and attribution
 * @param {string} walletAddress - User's wallet address
 * @param {string} toolKey - Tool identifier
 * @returns {Promise<object|null>} - Attribution data
 */
export async function handleWalletConnection(walletAddress, toolKey) {
  try {
    // Step 1: Check if wallet already has attribution
    const existingAttribution = await checkAttribution(walletAddress);
    
    if (existingAttribution) {
      console.log(`[Referral] Wallet already attributed to: ${existingAttribution.code}`);
      clearReferralCode(); // Clear localStorage since DB has it
      return existingAttribution;
    }
    
    // Step 2: Check localStorage for referral code
    const storedReferral = getReferralCode();
    
    if (!storedReferral) {
      console.log('[Referral] No referral code found');
      return null;
    }
    
    // Optional: Check if expired
    if (isReferralExpired()) {
      console.log('[Referral] Stored code has expired');
      clearReferralCode();
      return null;
    }
    
    // Step 3: Create attribution
    const attribution = await createAttribution(
      walletAddress,
      storedReferral.code,
      toolKey
    );
    
    if (attribution.success && attribution.attributed) {
      console.log(`[Referral] Attribution created: ${storedReferral.code}`);
      clearReferralCode(); // Clear after successful attribution
    } else {
      console.log(`[Referral] Attribution failed: ${attribution.message}`);
    }
    
    return attribution;
  } catch (error) {
    console.error('[Referral] Error handling wallet connection:', error);
    return null;
  }
}

// =====================================================
// Environment Configuration Helpers
// =====================================================

/**
 * Get Supabase URL from environment
 * IMPORTANT: Set VITE_SUPABASE_URL in your .env file
 */
function getSupabaseUrl() {
  return import.meta.env.VITE_SUPABASE_URL || '';
}

/**
 * Get Supabase Anon Key from environment
 * IMPORTANT: Set VITE_SUPABASE_ANON_KEY in your .env file
 */
function getSupabaseAnonKey() {
  return import.meta.env.VITE_SUPABASE_ANON_KEY || '';
}
